import React, { useMemo } from 'react';

import {
  Alert,
  Box,
  Divider,
  List, ListItemIcon, ListItemText, Stack, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';

import ContentCard, { ContentCardProps } from '../../../components/ContentCard';
import EventIcon from '../../../components/EventIcon';
import { ShortStaff } from '../../../services/staff/types';
import { UserGroup } from '../../../types/common';
import useUserGroups from '../../../utils/react/hooks/useUserGroups';
import { UPCOMING_BIRTHDAY_THRESHOLD_IN_DAYS } from '../constants';
import { NothingUpcomingText } from '../index.styles';

const TODAY = dayjs().startOf('day');

interface StaffBirthdaysCardProps extends Omit<ContentCardProps, 'children'> {
  staff?: ShortStaff[];
}

const StaffBirthdaysCard = ({ staff, ...props }: StaffBirthdaysCardProps) => {
  const theme = useTheme();
  const isMd = theme.breakpoints.up('md');

  const userGroups = useUserGroups() || [];

  const mappedStaff = useMemo(() => (staff || [])
    .map((x) => ({
      ...x,
      nextBirthday: x.birthday ? dayjs(x.birthday).year(TODAY.year()) : dayjs(),
      isBirthdayValid: !!x.birthday,
    })), [staff]);

  const upcomingBirthdayStaff = useMemo(() => mappedStaff
    .filter((x) => x.isBirthdayValid && x.nextBirthday.isSameOrAfter(TODAY) && x.nextBirthday.isSameOrBefore(TODAY.add(UPCOMING_BIRTHDAY_THRESHOLD_IN_DAYS, 'day')))
    .sort((a, b) => a.nextBirthday.diff(b.nextBirthday)),
  [mappedStaff]);

  const invalidBirthdayStaff = useMemo(() => mappedStaff.filter((x) => !x.isBirthdayValid),
    [mappedStaff]);

  return (
    <ContentCard
      variant="outlined"
      title="Upcoming Staff Birthdays 🥳"
      {...props}
    >
      <Stack direction="column" spacing={2}>
        {invalidBirthdayStaff.length > 0 && userGroups.includes(UserGroup.Managers) && (
          <Alert severity="warning">
            There are {invalidBirthdayStaff.length} staff members with invalid birthdays
            that must be investigated.
          </Alert>
        )}
        {upcomingBirthdayStaff.length > 0 ? (
          <List>
            {upcomingBirthdayStaff.map((s, i) => (
              <Stack>
                <Stack direction="row" justifyContent="center">
                  {isMd && (
                    <ListItemIcon>
                      <EventIcon
                        color="gray"
                        date={s.nextBirthday}
                        activeStartDate={s.nextBirthday.startOf('day')}
                        activeEndDate={s.nextBirthday.endOf('day')}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={s.name}
                    primaryTypographyProps={{ variant: 'h5' }}
                  />
                </Stack>
                {i < upcomingBirthdayStaff.length - 1 && (
                  <Box paddingTop={1} paddingBottom={1}>
                    <Divider />
                  </Box>
                )}
              </Stack>
            ))}
          </List>
        ) : (
          <NothingUpcomingText variant="body2" color="textSecondary">
            No upcoming birthdays
          </NothingUpcomingText>
        )}
      </Stack>
    </ContentCard>
  );
};

export default StaffBirthdaysCard;
